/**
 * Chapitre 4 — organiser la collecte des déchets.
 *
 * La compétence visée est **C4** : décrire l'organisation interne d'un système
 * et ses échanges avec son environnement, *énergies et données*. Ici les deux
 * tiennent dans le même geste — une donnée de remplissage, qui ne coûte rien à
 * produire, décide d'un déplacement de plusieurs dizaines de kilomètres.
 *
 * Le modèle de distance est volontairement simple : le camion suit une boucle,
 * `boucle_km_base` la parcourt à vide, et chaque conteneur retenu l'allonge de
 * son `km_detour`. Un élève de 4e peut le refaire de tête, ce qui est le but —
 * on veut qu'il raisonne sur l'ordre de grandeur, pas qu'il subisse un calcul.
 */
import type { Conteneur, Content, Tournee } from './content-schema.js';
import type { ActionResult, GameState } from './state.js';
import { refus, withAction } from './state.js';

/** La tournée que ce chapitre fait organiser, s'il y en a une. */
export function tourneeDuChapitre(content: Content, chapterId: string): Tournee | undefined {
  return content.tournees.find((t) => t.chapitre === chapterId);
}

/* ------------------------------------------------------------------ */
/* Étape 2 — Organiser                                                 */
/* ------------------------------------------------------------------ */

/**
 * Enregistre les arrêts d'un jour de sortie.
 *
 * Une liste vide est un choix légitime — le camion ne sort pas ce jour-là — et
 * se distingue d'un jour jamais traité, qui reste absent de la table.
 */
export function setTournee(
  state: GameState,
  content: Content,
  jour: string,
  conteneurs: readonly string[],
): ActionResult {
  const tournee = tourneeDuChapitre(content, state.chapterId);
  if (!tournee) {
    return refus(
      'probleme_inconnu',
      `Aucune tournée à organiser au chapitre « ${state.chapterId} ».`,
    );
  }
  if (!tournee.jours.includes(jour)) {
    return refus('choix_inconnu', `Le camion ne sort pas « ${jour} ».`);
  }

  const connus = new Set(tournee.conteneurs.map((c) => c.id));
  for (const id of conteneurs) {
    if (!connus.has(id)) {
      return refus('choix_inconnu', `Conteneur inconnu : « ${id} ».`);
    }
  }
  if (new Set(conteneurs).size !== conteneurs.length) {
    return refus('choix_inconnu', 'Un conteneur ne peut pas être collecté deux fois le même jour.');
  }

  return {
    ok: true,
    state: withAction(
      { ...state, tournees: { ...state.tournees, [jour]: [...conteneurs] } },
      { type: 'organiser_tournee', jour, conteneurs: [...conteneurs] },
    ),
  };
}

/** Tous les jours de sortie ont-ils été traités ? */
export function semaineOrganisee(state: GameState, content: Content): boolean {
  const tournee = tourneeDuChapitre(content, state.chapterId);
  if (!tournee) return false;
  return tournee.jours.every((jour) => state.tournees[jour] !== undefined);
}

/* ------------------------------------------------------------------ */
/* Le coût d'une sortie                                                */
/* ------------------------------------------------------------------ */

export interface CoutDeSortie {
  readonly km: number;
  readonly litres: number;
  readonly kgCo2: number;
}

/** Ce que coûte une sortie, d'après les arrêts retenus. Aucun arrêt, aucune sortie. */
export function coutDeSortie(tournee: Tournee, conteneurs: readonly string[]): CoutDeSortie {
  if (conteneurs.length === 0) {
    return { km: 0, litres: 0, kgCo2: 0 };
  }

  const detours = conteneurs.reduce((total, id) => {
    const conteneur = tournee.conteneurs.find((c) => c.id === id);
    return total + (conteneur?.km_detour ?? 0);
  }, 0);

  const km = tournee.camion.boucle_km_base + detours;
  const litres = (km * tournee.camion.litres_aux_cent) / 100;
  return { km, litres, kgCo2: litres * tournee.camion.kg_co2_par_litre };
}

/* ------------------------------------------------------------------ */
/* Étape 3 — Le bilan de la semaine                                    */
/* ------------------------------------------------------------------ */

export interface EtatConteneur {
  readonly id: string;
  readonly nom: string;
  /** Remplissage au matin de ce jour, avant la collecte. */
  readonly remplissage: number;
  readonly collecte: boolean;
  /** Le conteneur a-t-il dépassé 100 % sans être vidé ? */
  readonly deborde: boolean;
}

export interface JourDeCollecte {
  readonly jour: string;
  readonly conteneurs: readonly EtatConteneur[];
  readonly cout: CoutDeSortie;
  readonly debordements: number;
}

export interface BilanTournees {
  readonly jours: readonly JourDeCollecte[];
  readonly km: number;
  readonly litres: number;
  readonly kgCo2: number;
  readonly debordements: number;
  /** Les mêmes grandeurs pour la tournée fixe d'aujourd'hui, pour comparer. */
  readonly actuelle: CoutDeSortie;
  readonly message: string;
}

/** Ce que coûte la tournée fixe sur une semaine, telle que le contenu la décrit. */
export function coutActuel(tournee: Tournee): CoutDeSortie {
  const km = tournee.actuelle.km_par_sortie * tournee.actuelle.sorties_par_semaine;
  const litres = (km * tournee.camion.litres_aux_cent) / 100;
  return { km, litres, kgCo2: litres * tournee.camion.kg_co2_par_litre };
}

/**
 * Déroule la semaine, jour après jour.
 *
 * Les conteneurs se remplissent tous les jours, y compris ceux où le camion ne
 * sort pas. Un conteneur qui dépasse 100 % déborde : on le compte une fois, et
 * il reste plein jusqu'à sa collecte — un débordement ne se résorbe pas tout
 * seul, et c'est ce que les habitants voient.
 */
export function bilanDeLaSemaine(state: GameState, content: Content): BilanTournees {
  const tournee = tourneeDuChapitre(content, state.chapterId);
  if (!tournee) {
    const vide = { km: 0, litres: 0, kgCo2: 0 };
    return { jours: [], ...vide, debordements: 0, actuelle: vide, message: '' };
  }

  // Les jours de sortie, replacés dans la semaine : entre deux, les conteneurs
  // continuent de se remplir.
  const remplissage = new Map<string, number>(
    tournee.conteneurs.map((c: Conteneur) => [c.id, c.remplissage_initial]),
  );
  const dejaDeborde = new Set<string>();

  const jours = tournee.jours.map((jour, index): JourDeCollecte => {
    // Chaque sortie est séparée de la précédente par deux jours de remplissage ;
    // la première part de l'état du lundi matin.
    const joursEcoules = index === 0 ? 0 : 2;
    for (const conteneur of tournee.conteneurs) {
      const actuel = remplissage.get(conteneur.id) ?? 0;
      remplissage.set(conteneur.id, actuel + conteneur.vitesse_par_jour * joursEcoules);
    }

    const choisis = state.tournees[jour] ?? [];
    const etats = tournee.conteneurs.map((conteneur): EtatConteneur => {
      const niveau = remplissage.get(conteneur.id) ?? 0;
      const collecte = choisis.includes(conteneur.id);
      const deborde = niveau >= 100 && !dejaDeborde.has(conteneur.id);
      if (deborde) {
        dejaDeborde.add(conteneur.id);
      }
      if (collecte) {
        remplissage.set(conteneur.id, 0);
        dejaDeborde.delete(conteneur.id);
      }
      return { id: conteneur.id, nom: conteneur.nom, remplissage: niveau, collecte, deborde };
    });

    return {
      jour,
      conteneurs: etats,
      cout: coutDeSortie(tournee, choisis),
      debordements: etats.filter((e) => e.deborde).length,
    };
  });

  const debordements = jours.reduce((total, j) => total + j.debordements, 0);

  return {
    jours,
    km: jours.reduce((total, j) => total + j.cout.km, 0),
    litres: jours.reduce((total, j) => total + j.cout.litres, 0),
    kgCo2: jours.reduce((total, j) => total + j.cout.kgCo2, 0),
    debordements,
    actuelle: coutActuel(tournee),
    message:
      debordements === 0 ? tournee.feedback_sans_debordement : tournee.feedback_avec_debordement,
  };
}
